import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import FadeIn from "./FadeIn";

const HOW_STEPS = [
  {
    step: "1",
    title: "Tell us what you want",
    body: "Complete your survey once.",
    expanded: (
      <>
        <p className="text-sm text-gray-600 font-body mb-3">You'll answer a structured set of questions about:</p>
        <ul className="space-y-1.5 mb-3">
          <li className="text-sm text-gray-500 font-body flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-haevn-teal mt-2 flex-shrink-0" />what kind of connection you're looking for
          </li>
          <li className="text-sm text-gray-500 font-body flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-haevn-teal mt-2 flex-shrink-0" />your preferences and boundaries
          </li>
          <li className="text-sm text-gray-500 font-body flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-haevn-teal mt-2 flex-shrink-0" />how you like to engage
          </li>
        </ul>
        <p className="text-sm text-gray-500 font-body">This isn't a profile.<br />It's the foundation for matching.</p>
      </>
    ),
  },
  {
    step: "2",
    title: "We find your matches",
    body: "Only people aligned at 80%+.",
    expanded: (
      <>
        <p className="text-sm text-gray-600 font-body mb-3">Your responses are continuously compared across the network.</p>
        <p className="text-sm text-gray-500 font-body mb-3">You'll only see people who meet a minimum 80% alignment threshold.</p>
        <p className="text-sm text-gray-500 font-body">No browsing. No searching.<br />Just qualified matches.</p>
      </>
    ),
  },
  {
    step: "3",
    title: "You decide what's next",
    body: "See your matches. Connect if you want.",
    expanded: (
      <>
        <p className="text-sm text-gray-600 font-body mb-3">When matches are found, they're released on Match Monday.</p>
        <p className="text-sm text-gray-500 font-body mb-3">You'll see:</p>
        <ul className="space-y-1.5 mb-3">
          <li className="text-sm text-gray-500 font-body flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-haevn-teal mt-2 flex-shrink-0" />match percentage
          </li>
          <li className="text-sm text-gray-500 font-body flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-haevn-teal mt-2 flex-shrink-0" />shared intent
          </li>
          <li className="text-sm text-gray-500 font-body flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-haevn-teal mt-2 flex-shrink-0" />compatibility insights
          </li>
        </ul>
        <p className="text-sm text-gray-500 font-body">You choose if you want to take the next step.</p>
      </>
    ),
  },
];

export default function HowItWorksTiles() {
  const [expanded, setExpanded] = useState(null);

  const toggle = (step) => {
    setExpanded((prev) => (prev === step ? null : step));
  };

  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="how-it-works-tiles">
      {HOW_STEPS.map((item, i) => {
        const isOpen = expanded === item.step;
        return (
          <FadeIn key={item.step} delay={i * 120}>
            <div
              className="border-t-2 border-gray-200 pt-6 cursor-pointer select-none"
              onClick={() => toggle(item.step)}
              data-testid={`how-step-${item.step}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="w-10 h-10 rounded-full bg-haevn-teal/10 flex items-center justify-center mb-4">
                    <span className="font-headline text-haevn-teal">{item.step}</span>
                  </div>
                  <h3 className="font-subhead text-haevn-navy text-base mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 font-body">{item.body}</p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 mt-1 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </div>
              <div
                className={`overflow-hidden transition-all duration-250 ease-in-out ${isOpen ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0"}`}
              >
                <div className="border-l-2 border-haevn-teal/20 pl-4">
                  {item.expanded}
                </div>
              </div>
            </div>
          </FadeIn>
        );
      })}
    </div>
  );
}
